import { useEffect, useRef, useState } from 'react';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getFormFile, renditionOf, type FormDetail, type Rendition } from '../api/catalog';
import { ApiError } from '../api/httpClientFactory';
import { COPY } from '../copy';
import { browser } from '../utils/browser';
import { renditionLabel } from './labels';
import { recordViewOnce } from './recordView';

/** V7: the file name cnp-api itself would give, with the lower-case rendition. */
export function fileNameFor(code: string, rendition: Rendition): string {
  return `${code.replace(/[^A-Za-z0-9_-]/g, '_')}-${rendition}.pdf`;
}

interface DownloadItem {
  served: string;
  rendition: Rendition;
}

/** V6: one button per offered PDF rendition; one download at a time (ACT-008). */
export function FormDownloads({ form }: { form: FormDetail }) {
  const [busy, setBusy] = useState<Rendition | null>(null);
  const [error, setError] = useState<{ served: string; error: ApiError } | null>(null);
  const mounted = useRef(true);
  // A synchronous guard as well as `busy`: two clicks in one tick both see the old `busy`.
  const inFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const items: DownloadItem[] = form.renditions.flatMap((served) => {
    const rendition = renditionOf(served);
    return rendition ? [{ served, rendition }] : [];
  });
  if (items.length === 0) return null;

  const onDownload = async ({ served, rendition }: DownloadItem) => {
    if (busy !== null || inFlight.current) return;
    inFlight.current = true;
    setBusy(rendition);
    setError(null);
    try {
      const blob = await getFormFile(form.id, rendition);
      const url = URL.createObjectURL(blob);
      browser.download(url, fileNameFor(form.code, rendition));
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      recordViewOnce(form.id, form.versionNo);
    } catch (reason: unknown) {
      const apiError = reason instanceof ApiError ? reason : new ApiError('UNKNOWN', 0);
      if (apiError.code !== 'SESSION_EXPIRED' && mounted.current) {
        setError({ served, error: apiError });
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(null);
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
        role="group"
        aria-label={COPY.downloadsLabel}
      >
        {items.map((item) => {
          const isBusy = busy === item.rendition;
          return (
            <Button
              key={item.rendition}
              variant="outlined"
              size="small"
              startIcon={
                isBusy ? (
                  <CircularProgress size="1em" color="inherit" aria-hidden />
                ) : (
                  <DownloadOutlined />
                )
              }
              disabled={busy !== null}
              aria-busy={isBusy ? 'true' : undefined}
              onClick={() => void onDownload(item)}
            >
              {COPY.download(renditionLabel(item.served))}
            </Button>
          );
        })}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {COPY.downloadHint}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <Typography variant="body2">
            {COPY.downloadError(renditionLabel(error.served))}
          </Typography>
          <Typography variant="body2">
            {COPY.reference(error.error.requestId ?? error.error.code)}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
